/* ======================================================================
   Cart Page Upsells - Complete Your Bundle
   Shows snack upsells on cart page when bundle items are present
   ====================================================================== */

(function() {
  'use strict';

  /* On. Was switched off on 2026-09-01 and brought back the same day when the
     section was reworked; set to false to hide it again.

     A switch rather than a deletion: the strip, its two modals and their
     styling in assets/tlh-cart.css are all still here and current, so this is
     the only line that has to change. Nothing else on the page depends on it
     — /cart is the only route it ever rendered on.

     It returns before any network work, so the two collection fetches and the
     five per-product lookups the section used to make no longer run. */
  const ENABLED = true;

  // Helper functions
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  // Configuration
  // Badges carry no emoji: nothing else in the store's language does, and next
  // to Arvo small-caps they read as a different voice.
  const UPSELL_PRODUCTS = [
    { title: 'Crunchy Mung Snack - Salted', badge: '30% of customers add this' },
    { title: 'Crunchy Mung Snack - Spiced', badge: 'Trending' },
    { title: 'Popped Water Lily Seeds - Salted', badge: 'Customer favourite' },
    { title: 'Popped Water Lily Seeds - Cheddar Cheese', badge: 'Popular choice' },
    { title: 'Popped Water Lily Seeds - Tangy Tomato', badge: 'Flavour favourite' }
  ];

  // Toast notification
  function showToast(msg) {
    const existing = document.querySelector('.tlh-cart-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'tlh-cart-toast';
    toast.textContent = msg;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // Check if we're on cart page and if bundle items exist
  async function shouldShowUpsells() {
    if (!ENABLED) {
      return false;
    }

    // Check if we're on cart page
    if (!window.location.pathname.includes('/cart')) {
      return false;
    }

    try {
      // Fetch cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Check if any items have bundle properties
      const hasBundleItems = cart.items.some(item =>
        item.properties && item.properties['_bundle_id']
      );

      return hasBundleItems;
    } catch (error) {
      console.error('Error checking cart:', error);
      return false;
    }
  }

  // Update cart UI after adding product
  async function updateCartUI() {
    try {
      // Fetch updated cart page HTML
      const pageResponse = await fetch('/cart');
      const html = await pageResponse.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart holder (the items section)
      const currentCartHolder = $('.cart-holder');
      const newCartHolder = doc.querySelector('.cart-holder');
      if (currentCartHolder && newCartHolder) {
        currentCartHolder.innerHTML = newCartHolder.innerHTML;
      }

      // Update the entire sidebar section (includes shipping notice and total)
      const currentAside = $('aside');
      const newAside = doc.querySelector('aside');
      if (currentAside && newAside) {
        currentAside.innerHTML = newAside.innerHTML;
      }

      // Fetch cart JSON to update cart count in header
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Update cart item count in header/mini cart
      const cartCountElements = $$('[data-cart-count], .cart-count, .cart__count, #CartCount');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });

      // Trigger cart update event for other scripts
      window.dispatchEvent(new Event('cart:updated'));
      document.dispatchEvent(new Event('cart:updated'));
    } catch (error) {
      console.error('Error updating cart UI:', error);
    }
  }

  // Add product to cart
  async function addToCart(variantId, productTitle) {
    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: 1
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showToast(`${productTitle} added to cart`);

        // Update cart UI without reloading
        await updateCartUI();
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast('Unable to add product. Please try again.');
    }
  }

  // Add multiple products to cart (for 2-pack)
  async function addMultipleToCart(items, packTitle) {
    try {
      const formData = {
        items: items.map(item => ({
          id: item.variantId,
          quantity: 1
        }))
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showToast(`${packTitle} added to cart`);

        // Update cart UI without reloading
        await updateCartUI();
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast('Unable to add products. Please try again.');
    }
  }

  // Fetch upsell products
  async function fetchUpsellProducts() {
    try {
      // Fetch both collections
      const [mungResponse, lotusResponse] = await Promise.all([
        fetch('/collections/high-protein-lentil-snacks/products.json'),
        fetch('/collections/lotus-seeds/products.json')
      ]);

      const mungData = await mungResponse.json();
      const lotusData = await lotusResponse.json();

      const allProducts = [...mungData.products, ...lotusData.products];
      const selectedProducts = [];

      // Find matching products and fetch full product data
      for (const pref of UPSELL_PRODUCTS) {
        const product = allProducts.find(p =>
          p.title.toLowerCase().includes(pref.title.toLowerCase())
        );
        if (product) {
          // Fetch full product data to get correct pricing
          try {
            const fullProductResponse = await fetch(`/products/${product.handle}.js`);
            const fullProduct = await fullProductResponse.json();
            selectedProducts.push({ product: fullProduct, badge: pref.badge });
          } catch (error) {
            console.error(`Failed to fetch full product for ${product.handle}:`, error);
            selectedProducts.push({ product, badge: pref.badge });
          }
        }
      }

      return selectedProducts;
    } catch (error) {
      console.error('Error fetching upsell products:', error);
      return [];
    }
  }

  // Render product modal
  function renderProductModal(product) {
    // Check if modal already exists
    let modal = $('#cart-upsell-modal');
    if (!modal) {
      // Create modal structure
      modal = document.createElement('div');
      modal.id = 'cart-upsell-modal';
      modal.className = 'cart-upsell-modal';
      modal.setAttribute('data-shopify-track', 'false');
      modal.innerHTML = `
        <div class="cart-upsell-modal-overlay"></div>
        <div class="cart-upsell-modal-content" data-shopify-track="false"></div>
      `;
      document.body.appendChild(modal);

      // Add modal close handlers
      $('.cart-upsell-modal-overlay', modal).addEventListener('click', () => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      });
    }

    // Helper to fix image URLs (add https: if missing)
    const fixImageUrl = (url) => {
      if (!url) return '';
      return url.startsWith('//') ? `https:${url}` : url;
    };

    // Build modal content
    const price = (product.variants[0].price / 100).toFixed(2);
    const images = (product.images || []).map(img =>
      typeof img === 'string' ? fixImageUrl(img) : fixImageUrl(img?.src || '')
    ).filter(url => url);
    const mainImage = images[0] || fixImageUrl(product.featured_image);

    // Get description - try description first, then body_html
    const description = product.description || product.body_html || '';

    const modalContent = $('.cart-upsell-modal-content', modal);
    modalContent.innerHTML = `
      <div class="cart-upsell-modal__body">
        <div class="cart-upsell-modal__head">
          <h2 class="cart-upsell-modal__title">${product.title}</h2>
          <button class="cart-upsell-modal-close" type="button" aria-label="Close">&times;</button>
        </div>
        <div class="cart-upsell-modal__cols">
          <div>
            <div class="cart-upsell-modal__plate">
              <img src="${mainImage}" alt="${product.title}">
            </div>
            ${images.length > 1 ? `
              <div class="cart-upsell-modal__thumbs">
                ${images.slice(0, 4).map((imgUrl, i) => `
                  <img src="${imgUrl}" alt="${product.title}"
                       class="cart-upsell-thumb${i === 0 ? ' is-active' : ''}" data-index="${i}">
                `).join('')}
              </div>
            ` : ''}
          </div>
          <div>
            <div class="cart-upsell-modal__price">$${price}</div>
            ${description ? `<div class="cart-upsell-modal__desc">${description}</div>` : ''}
            <button class="cart-upsell-btn cart-upsell-modal-add" data-variant-id="${product.variants[0].id}" data-product-title="${product.title}" data-shopify-track="false">
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    `;

    // Add close button handler
    $('.cart-upsell-modal-close', modal).addEventListener('click', () => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    });

    // Add image thumbnail handlers
    $$('.cart-upsell-thumb', modal).forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        const mainImg = modalContent.querySelector('img');
        mainImg.src = images[index];
        $$('.cart-upsell-thumb', modal).forEach((t, i) => {
          t.classList.toggle('is-active', i === index);
        });
      });
    });

    // Add "Add to Cart" handler in modal
    $('.cart-upsell-modal-add', modal).addEventListener('click', async (e) => {
      const btn = e.target;
      const variantId = btn.dataset.variantId;
      const productTitle = btn.dataset.productTitle;

      btn.disabled = true;
      btn.textContent = 'Adding...';

      await addToCart(variantId, productTitle);

      // Reset button before closing (in case there's a delay)
      btn.disabled = false;
      btn.textContent = 'Add to Cart';

      // Close modal after adding
      modal.style.display = 'none';
      document.body.style.overflow = '';
    });

    // Show modal
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  // Render combo modal (for 2-pack)
  function renderComboModal(product1, product2) {
    // Check if modal already exists
    let modal = $('#cart-upsell-combo-modal');
    if (!modal) {
      // Create modal structure
      modal = document.createElement('div');
      modal.id = 'cart-upsell-combo-modal';
      modal.className = 'cart-upsell-modal';
      modal.setAttribute('data-shopify-track', 'false');
      modal.innerHTML = `
        <div class="cart-upsell-modal-overlay"></div>
        <div class="cart-upsell-modal-content cart-upsell-combo-modal-content" data-shopify-track="false"></div>
      `;
      document.body.appendChild(modal);

      // Add modal close handlers
      $('.cart-upsell-modal-overlay', modal).addEventListener('click', () => {
        modal.style.display = 'none';
        document.body.style.overflow = '';
      });
    }

    // Helper to fix image URLs (add https: if missing)
    const fixImageUrl = (url) => {
      if (!url) return '';
      return url.startsWith('//') ? `https:${url}` : url;
    };

    // Get product details
    const price1 = (product1.variants[0].price / 100).toFixed(2);
    const price2 = (product2.variants[0].price / 100).toFixed(2);
    const totalPrice = (parseFloat(price1) + parseFloat(price2)).toFixed(2);

    const image1 = fixImageUrl(product1.images[0]?.src || product1.featured_image || '');
    const image2 = fixImageUrl(product2.images[0]?.src || product2.featured_image || '');

    const desc1 = product1.description || product1.body_html || '';
    const desc2 = product2.description || product2.body_html || '';

    const modalContent = $('.cart-upsell-combo-modal-content', modal);
    modalContent.innerHTML = `
      <div class="cart-upsell-modal__body">
        <div class="cart-upsell-modal__head">
          <div>
            <span class="cart-upsell-card__flag cart-upsell-card__flag--static">Best value combo</span>
            <h2 class="cart-upsell-modal__title">Crunchy Mung 2-Pack</h2>
          </div>
          <button class="cart-upsell-combo-modal-close" type="button" aria-label="Close">&times;</button>
        </div>

        <div class="combo-products-grid">
          <!-- Product 1 -->
          <div class="combo-product-card">
            <div class="cart-upsell-modal__plate">
              <img src="${image1}" alt="${product1.title}">
            </div>
            <h3 class="cart-upsell-card__title">${product1.title}</h3>
            <div class="cart-upsell-card__price">$${price1}</div>
            ${desc1 ? `<div class="cart-upsell-modal__desc cart-upsell-modal__desc--clamped">${desc1}</div>` : ''}
          </div>

          <!-- Product 2 -->
          <div class="combo-product-card">
            <div class="cart-upsell-modal__plate">
              <img src="${image2}" alt="${product2.title}">
            </div>
            <h3 class="cart-upsell-card__title">${product2.title}</h3>
            <div class="cart-upsell-card__price">$${price2}</div>
            ${desc2 ? `<div class="cart-upsell-modal__desc cart-upsell-modal__desc--clamped">${desc2}</div>` : ''}
          </div>
        </div>

        <!-- Combined pricing and add to cart -->
        <div class="cart-upsell-combo__total">
          <div class="cart-upsell-combo__total-line">
            <span class="cart-upsell-combo__total-label">2-Pack total</span>
            <span class="cart-upsell-combo__total-value">$${totalPrice}</span>
          </div>
          <button
            class="cart-upsell-btn cart-upsell-combo-modal-add"
            data-variant1="${product1.variants[0].id}"
            data-variant2="${product2.variants[0].id}"
            data-shopify-track="false">
            Add Both to Cart
          </button>
          <div class="cart-upsell-card__note">Most popular combo — get both flavors</div>
        </div>
      </div>

      <style>
        /* The orange hover that used to sit here -- #e7611f on white, plus a
           scale(1.02) nothing else in the store does -- is gone. It was the
           last of the palette assets/tlh-cart.css says it took over, and it
           was overriding that file's own hover for this button, which turns it
           to olive on the surface cream like every other .cart-upsell-btn. */
        @media (max-width: 600px) {
          .cart-upsell-combo-modal-content {
            width: 100% !important;
            max-width: 100% !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
          }
          .cart-upsell-combo-modal-content > div {
            padding: 1rem !important;
          }
          .cart-upsell-combo-modal-content h2 {
            font-size: 1.25rem !important;
          }
          .combo-products-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .combo-product-card {
            padding: 1rem !important;
          }
          /* No max-height on the images: they are the oval's plate art now,
             absolutely positioned and sized off the stadium, so capping them
             at 150px only broke the framing on phones. */
          .cart-upsell-combo-modal-add {
            width: 100% !important;
            padding: 1rem !important;
          }
        }
      </style>
    `;

    // Add close button handler
    $('.cart-upsell-combo-modal-close', modal).addEventListener('click', () => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
    });

    // Add "Add Both to Cart" handler
    $('.cart-upsell-combo-modal-add', modal).addEventListener('click', async (e) => {
      const btn = e.target;
      const variantId1 = btn.dataset.variant1;
      const variantId2 = btn.dataset.variant2;

      btn.disabled = true;
      btn.textContent = 'Adding...';

      await addMultipleToCart([
        { variantId: variantId1 },
        { variantId: variantId2 }
      ], 'Crunchy Mung 2-Pack');

      // Reset button before closing
      btn.disabled = false;
      btn.textContent = 'Add Both to Cart';

      // Close modal after adding
      modal.style.display = 'none';
      document.body.style.overflow = '';
    });

    // Show modal
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  // Render upsell section
  function renderUpsellSection(products) {
    const cartForm = $('form[action="/cart"]') || $('.cart') || $('main');
    if (!cartForm) return;

    // Check if section already exists
    if ($('.cart-upsell-section')) return;

    // Store products for modal access
    window.cartUpsellProducts = products;

    // Create 2-pack from first two products (Crunchy Mung Snacks)
    const twoPackProducts = products.slice(0, 2);
    const hasTwoPack = twoPackProducts.length === 2;
    let twoPackHtml = '';

    if (hasTwoPack) {
      const product1 = twoPackProducts[0].product;
      const product2 = twoPackProducts[1].product;
      const price1 = product1.variants[0].price / 100;
      const price2 = product2.variants[0].price / 100;
      const totalPrice = (price1 + price2).toFixed(2);
      const imageUrl1 = product1.images[0]?.src || product1.featured_image;
      const imageUrl2 = product2.images[0]?.src || product2.featured_image;

      twoPackHtml = `
        <div class="cart-upsell-card cart-upsell-2pack" data-is2pack="true" data-shopify-track="false">
          <span class="cart-upsell-card__flag">Best value</span>
          <div class="cart-upsell-card__plate cart-upsell-card__plate--pair">
            <img src="${imageUrl1}" alt="${product1.title}">
            <img src="${imageUrl2}" alt="${product2.title}">
          </div>
          <h3 class="cart-upsell-card__title">Crunchy Mung 2-Pack</h3>
          <div class="cart-upsell-card__price">$${totalPrice}</div>
          <button
            class="cart-upsell-btn cart-upsell-2pack-btn"
            data-variant1="${product1.variants[0].id}"
            data-variant2="${product2.variants[0].id}"
            data-shopify-track="false">
            Add Both to Cart
          </button>
          <div class="cart-upsell-card__note">Most popular combo</div>
        </div>
      `;
    }

    // Create section
    const section = document.createElement('div');
    section.className = 'cart-upsell-section';
    section.setAttribute('data-shopify-track', 'false');

    section.innerHTML = `
      <h2 class="cart-upsell__heading">Customers also bought</h2>
      <p class="cart-upsell__subheading">Pair these with your bundle</p>

      <div class="cart-upsell__viewport">
        <button class="cart-upsell-prev" type="button" aria-label="Previous">&lsaquo;</button>
        <button class="cart-upsell-next" type="button" aria-label="Next">&rsaquo;</button>

        <div class="cart-upsell-carousel">
          <div class="cart-upsell-grid">
            ${twoPackHtml}
            ${products.map(({ product, badge }, index) => {
              const price = (product.variants[0].price / 100).toFixed(2);
              const imageUrl = product.images[0]?.src || product.featured_image;

              return `
                <div class="cart-upsell-card" data-product-index="${index}" data-shopify-track="false">
                  <div class="cart-upsell-card__plate">
                    <img src="${imageUrl}" alt="${product.title}">
                  </div>
                  <h3 class="cart-upsell-card__title">${product.title}</h3>
                  <div class="cart-upsell-card__price">$${price}</div>
                  <button
                    class="cart-upsell-btn"
                    data-variant-id="${product.variants[0].id}"
                    data-product-title="${product.title}"
                    data-shopify-track="false">
                    Add to Cart
                  </button>
                  <div class="cart-upsell-card__note">${badge}</div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      </div>
    `;

    // Presentation lives in assets/tlh-cart.css, which main-cart.liquid
    // already loads on /cart -- the only route this section renders on.

    // Stays inside the form, which is to say inside the grid's first track:
    // the strip belongs under the products, at the products' width, and leaves
    // the summary column to itself.
    //
    // Appending it to .cart-section instead would make it a grid item and let
    // it span both tracks, but a grid row starts below the TALLEST item in the
    // row above, so whenever the summary outruns the item list the strip would
    // sit below the wallet buttons with a band of empty cream under the last
    // product. Here it follows the items directly, whatever either column does.
    const checkoutBtn = $('[name="checkout"]') || $('.cart__checkout');
    if (checkoutBtn && checkoutBtn.closest('.cart__footer')) {
      checkoutBtn.closest('.cart__footer').insertAdjacentElement('beforebegin', section);
    } else {
      cartForm.appendChild(section);
    }

    // Initialize carousel (add 1 for the 2-pack card if it exists)
    const totalCards = hasTwoPack ? products.length + 1 : products.length;
    initializeCarousel(section, totalCards);

    // Add click handlers for cards (view product details)
    $$('.cart-upsell-card', section).forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't open modal if clicking the button
        if (e.target.closest('.cart-upsell-btn')) return;

        // Check if this is a 2-pack card - show combo modal
        if (card.dataset.is2pack === 'true') {
          const twoPackProducts = window.cartUpsellProducts.slice(0, 2);
          if (twoPackProducts.length === 2) {
            renderComboModal(twoPackProducts[0].product, twoPackProducts[1].product);
          }
          return;
        }

        const productIndex = parseInt(card.dataset.productIndex);
        const productData = window.cartUpsellProducts[productIndex];
        if (productData) {
          renderProductModal(productData.product);
        }
      });
    });

    // Add click handler for 2-pack button
    const twoPackBtn = $('.cart-upsell-2pack-btn', section);
    if (twoPackBtn) {
      twoPackBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId1 = twoPackBtn.dataset.variant1;
        const variantId2 = twoPackBtn.dataset.variant2;
        const originalText = twoPackBtn.textContent;

        twoPackBtn.disabled = true;
        twoPackBtn.textContent = 'Adding...';

        await addMultipleToCart([
          { variantId: variantId1 },
          { variantId: variantId2 }
        ], 'Crunchy Mung 2-Pack');

        // Reset button state after adding
        twoPackBtn.disabled = false;
        twoPackBtn.textContent = originalText;
      });
    }

    // Add click handlers for buttons (add to cart)
    $$('.cart-upsell-btn:not(.cart-upsell-2pack-btn)', section).forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent card click event

        const variantId = btn.dataset.variantId;
        const productTitle = btn.dataset.productTitle;
        const originalText = btn.textContent;

        btn.disabled = true;
        btn.textContent = 'Adding...';

        await addToCart(variantId, productTitle);

        // Reset button state after adding
        btn.disabled = false;
        btn.textContent = originalText;
      });
    });
  }

  // Initialize carousel navigation
  function initializeCarousel(section, totalProducts) {
    let currentIndex = 0;
    const grid = $('.cart-upsell-grid', section);
    const prevBtn = $('.cart-upsell-prev', section);
    const nextBtn = $('.cart-upsell-next', section);
    const cards = $$('.cart-upsell-card', section);
    const carousel = $('.cart-upsell-carousel', section);

    if (!grid || !prevBtn || !nextBtn || !cards.length || !carousel) return;

    function updateCarousel() {
      const isMobile = window.innerWidth <= 767;   // matches the stylesheet
      const containerWidth = carousel.offsetWidth;
      const cardWidth = cards[0].offsetWidth;
      // Read from the stylesheet rather than repeating it: the gap is set in
      // assets/tlh-cart.css and the two used to be able to drift apart.
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 24;

      // How many cards fit. The last one in view has no trailing gap, so the
      // track holds (containerWidth + gap) / (cardWidth + gap) of them --
      // without that correction a two-up row measured as one and the arrows
      // stepped through the strip a card at a time.
      const actuallyFits = Math.round((containerWidth + gap) / (cardWidth + gap));
      const visibleProducts = isMobile ? 1 : Math.max(1, actuallyFits);
      const maxIndex = Math.max(0, totalProducts - visibleProducts);

      // Clamp current index
      currentIndex = Math.max(0, Math.min(currentIndex, maxIndex));

      let offset;
      if (currentIndex >= maxIndex && maxIndex > 0) {
        // At the end, ensure the last card is fully visible
        // Get the actual position of the last card
        const lastCard = cards[totalProducts - 1];
        const lastCardRight = lastCard.offsetLeft + lastCard.offsetWidth;
        // Calculate offset to align the last card's right edge with container's right edge
        offset = Math.max(0, lastCardRight - containerWidth);
      } else {
        // Normal scrolling by card index
        offset = currentIndex * (cardWidth + gap);
      }

      grid.style.transform = `translateX(-${offset}px)`;

      // Update button states
      prevBtn.style.opacity = currentIndex === 0 ? '0.5' : '1';
      prevBtn.style.cursor = currentIndex === 0 ? 'not-allowed' : 'pointer';
      prevBtn.disabled = currentIndex === 0;
      nextBtn.style.opacity = currentIndex >= maxIndex ? '0.5' : '1';
      nextBtn.style.cursor = currentIndex >= maxIndex ? 'not-allowed' : 'pointer';
      nextBtn.disabled = currentIndex >= maxIndex;
    }

    // Previous button click handler
    prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (currentIndex > 0) {
        currentIndex--;
        updateCarousel();
      }
    });

    // Next button click handler
    nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const isMobile = window.innerWidth <= 767;   // matches the stylesheet
      const containerWidth = carousel.offsetWidth;
      const cardWidth = cards[0].offsetWidth;
      const gap = 24;
      const actuallyFits = Math.floor(containerWidth / (cardWidth + gap));
      const visibleProducts = isMobile ? 1 : Math.max(1, actuallyFits);
      const maxIndex = totalProducts - visibleProducts;
      if (currentIndex < maxIndex) {
        currentIndex++;
        updateCarousel();
      }
    });

    // Update on resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        currentIndex = 0;
        updateCarousel();
      }, 150);
    });

    // Initial update
    updateCarousel();
  }

  // Initialize
  async function init() {
    // Check if we should show upsells
    const show = await shouldShowUpsells();
    if (!show) return;

    // Fetch and render products
    const products = await fetchUpsellProducts();
    if (products.length > 0) {
      renderUpsellSection(products);
    }
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
